/* eslint-disable react-hooks/set-state-in-effect -- §9.807: five effects that predate this change, never held to the rule on main (the compiler skipped this component); fixing them is owed with the tests. */
import type { FiskalReceiptPrinted } from '@agos/api-client/model'
import { apiErrorMessage } from '@agos/api-client'
import { filterChannelsOf, formatCount, parseMoneyToCents, printRegistersOf, useModalFocus } from '@agos/shared'
import { postFiskalReceipt, useGetFiskalRegisters } from '@agos/api-client/fiskal'
import { postCouponsValidate } from '@agos/api-client/menu'
import {
  patchOrdersOrderId,
  postOrdersOrderIdCloseWithoutDocument,
  postOrdersSplitPayment,
} from '@agos/api-client/orders'
import { useGetEmployeesWaiters } from '@agos/api-client/hr'
// §9.148 - *na sekeru* at the moment they ask for it, which is here rather than on the tabs screen.
import {
  useGetOrderTabs,
  usePostOrderTabs,
  usePostOrderTabsIdOrdersOrderId,
} from '@agos/api-client/orders'
import { useGetSalesChannels } from '@agos/api-client/orders'
// §9.806, owner O153 - loyalty points on the bill.
import {
  deleteOrdersOrderIdLoyaltyPoints,
  postOrdersOrderIdLoyaltyPoints,
  postOrdersOrderIdLoyaltyPointsQuote,
  useGetOrdersOrderIdLoyaltyPoints,
} from '@agos/api-client/orders'
import {
  codeDigits,
  editedDiscountWithPoints,
  onTheBillNote,
  zlavaWithoutPointsCents,
  type PointsOnTheBill,
} from './table-points'
import {
  closerEmployeeId,
  initialPayWaiterId,
  payWaiterProblem,
  paymentAsksWhoTookTheMoney,
  paymentStartsWithTheCloser,
  waiterOptions,
} from '../order-builder/waiter-choice'
import type { OrderListItem } from '@agos/api-client/model'
import {
  FiskalStornoAction,
  T,
  alreadyPrinted,
  alreadyPrintedNote,
  centsToEuros,
  formatCents,
  lineTotalCents,
  openTabChoices,
  printVirtualReceipt,
  registerIds,
  registerName,
  SHIPPED_REGISTER_NAMES,
  naSekeruEnabled,
  tabRefusal,
  tt,
  useAuth,
} from '@agos/shared'
import { cn, toast } from '@agos/ui'
import {
  Ban,
  Banknote,
  CreditCard,
  Loader2,
  Minus,
  NotebookPen,
  Plus,
  Receipt,
  Split,
  X,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  NO_DISCOUNT,
  couponPayload,
  closeDiscountPayload,
  discountNote,
  discountProblems,
  payableCents,
  totalDiscountCents,
  type DiscountMode,
  type PayDiscount,
} from './pay-discount'
import {
  billRows,
  editedDiscountPayload,
  existingDiscountNote,
  outstandingCents,
  overrideIsSpent,
  pricedWith,
} from './outstanding'
import {
  alreadyPaid,
  alreadyPaidNote,
  closeTableQuestion,
  closeWithoutDocumentBody,
  closeWithoutDocumentNote,
  closedByName,
  closedOutcome,
  closeWithoutDocumentTitle,
  mayCloseWithoutDocument,
  hasSomethingToPay,
  isTransientKasaWarning,
  lineKey,
  receiptLines,
  splitSelection,
  unprintedCloseOf,
  wholeBill,
  type PayableLine,
} from './split-payment'
import { CloseWithoutDocumentConfirm } from './CloseWithoutDocumentConfirm'
import { PaymentInFlightView } from './PaymentInFlight'
import {
  discountAfterPrintNote,
  discountKeptNote,
  frozenLines,
  orderChangedNote,
  paidPortionOf,
  recordedDiscountCents,
  recordedDiscountPayload,
  sameBill,
  type PaymentInFlight,
} from './payment-in-flight'
import { discountBoxesFor } from '../order-rights'
import { kasaProblem, registerChoice } from './register-choice'

/**
 * Settling a table.
 *
 * Two ways out: the whole bill at once, or a split where a leaving guest pays
 * for the lines they had. The split closes the original order and re-issues it
 * as a paid one and a still-open one, and prints a receipt for the paid half
 * only - `splitSelection` decides the division and is tested separately,
 * because a quantity on the wrong side either charges somebody twice or lets a
 * table walk.
 *
 * Ported from legacy `pages/restaurant.tsx`.
 */

const PAYMENT_METHODS = [
  { value: 'cash', label: tt('Hotovosť'), icon: Banknote },
  { value: 'card_terminal', label: tt('Karta – terminál'), icon: CreditCard },
] as const

/** Every eight seconds, so a table cancelled in the kitchen is noticed. */
export const PAYMENT_POLL_MS = 8000

/**
 * §11.4 - in virtual mode the register never printed anything, so the browser
 * does. Legacy only raised a toast here, which left the customer with no receipt.
 *
 * Outside the component on purpose: it reads the clock, and a function that reads
 * the clock has no business being defined in a render body.
 */
function printIfVirtual(response: FiskalReceiptPrinted): void {
  if (response.virtualReceipt !== true || !response.receipt) return
  const printed = printVirtualReceipt(response.receipt, Date.now())
  if (printed) return
  toast({
    variant: 'destructive',
    title: tt('Doklad sa nepodarilo otvoriť'),
    description: tt('Povoľte vyskakovacie okná a doklad vytlačte znova.'),
  })
}

/** The names the port ships with, drawn until the franchise types one over a register - §10A.35. */

export function PaymentDialog({
  order,
  closer,
  onClose,
  onPaid,
  onOrderChanged,
}: {
  order: OrderListItem
  /**
   * Who owns the money once this closes - `FRONTEND.md` §9.740, owner O111.
   *
   * - `'asked'` - the till (*Otvorené stoly*, the order builder): *Čašník / obsluha* where the channel needs it,
   *   exactly as §9.128 and §9.736 settled.
   * - `'whoever-closes'` - the dispatch board's *Vydaná*, where the owner asked for the order to be *"assigned to
   *   someone, who use Vydaná button"*: no question and no `employeeId`, so the server credits the signed-in person
   *   (`BACKEND.md` §10A.386's session rule, the same answer the board's own close gives).
   *
   * **Required**, so a third caller has to say which it is rather than inheriting the till's question by default -
   * §9.480's rule for a parameter every call site is ours.
   */
  closer: 'asked' | 'whoever-closes'
  onClose: () => void
  onPaid: () => void
  /**
   * The order itself was written - §9.464. Optional, because one of the two callers unmounts this
   * dialog on the way to another screen and has nothing to refresh; the override in
   * {@link pricedWith} is what keeps the bill right for the caller that does not reload.
   */
  onOrderChanged?: () => void
}) {
  const [method, setMethod] = useState<string>('cash')
  /* §9.579, FB-883 - whether a person has touched the selector, which `method` cannot say for itself:
   * `'cash'` is both the initial state and a legal choice, and treating the initial value as *nobody
   * has chosen* is the trap §9.535 recorded when a `waitFor` matched a state that had not loaded. */
  const [methodChosen, setMethodChosen] = useState(false)
  // §9.128 - who is taking the money. Asked here and not only at order time, because on a shared
  // till the person who opened the table is routinely not the person settling it.
  const [payWaiterId, setPayWaiterId] = useState<number | null>(null)
  /* §9.652, FB-981 - the channel list is read **here**, above the waiter query, because that query's
     `enabled` asks it. A `const` is not hoisted, so leaving the read where it was is TS2448 rather than a
     silent nothing - §9.487's hazard, and the reason this moved rather than being duplicated. */
  const { data: channelData } = useGetSalesChannels()
  const channelList = useMemo(() => (Array.isArray(channelData) ? channelData : []), [channelData])
  const channel = useMemo(
    () =>
      (Array.isArray(channelData) ? channelData : []).find(
        (one) => (one as { slug?: string }).slug === order.type,
      ) as
        | {
            name?: string
            allowsDeferredPayment?: boolean
            vatSlot?: string | null
            onPremise?: boolean
          }
        | undefined,
    [channelData, order.type],
  )
  /* §9.726, owner O96 - what *Zatvoriť bez fiškálneho dokladu* will do, asked **once** and drawn by both the note
     under the button and the question, so the two cannot disagree. Through the parsed channels rather than the raw
     row above, because `filterChannelsOf` is where `unprintedCloseCancels` reads as §9.457's cancellation on a server
     older than V79 - the raw row would leave that to whoever reads it next. */
  const unprintedClose = useMemo(
    () => unprintedCloseOf(order.type ?? '', filterChannelsOf(channelData)),
    [channelData, order.type],
  )
  // The gate the server enforces in `OrderTabService.append`. Said on the screen rather than
  // discovered as a rejected request - OWNER-QUESTIONS §9 is the switch that opens it.
  /* §9.281, owner O20b - the slate is refused outright when its own channel is switched off. That is
   * a different question from whether *this* order's channel permits a deferred payment: `restaurant`
   * does, which is why the button showed while the feature was off. */
  // §9.299 - the owner asked twice for this to be *hidden* when the channel is off, not disabled with
  // a reason. The two refusals are different messages: "not for this order" is true while the slate
  // runs, and "not here at all" is what a franchise that does not run one should see - which is
  // nothing. Read from the channel rather than by matching `tabRefusal`'s sentence, which would break
  // the first time one is reworded.
  /* §9.736, owner O106, FB-1042 - whether this till asks who took the money, asked **once** and read by the
     shift-list fetch, the preselection and the drawn question, so the three cannot disagree. The till's own rule
     rather than `paymentNeedsWaiter`, the server's mirror: §9.652 moved the till onto that one, `bar` has no
     tables, and from 2026-10-05 a pickup was closed with nobody asked and nobody owning the money.
     §9.652 still holds - the channels, not the two slugs the fallback knows - or a terrace table is never asked
     and `OrderStatusService.guardClosable` refuses the close with no control on screen to answer it. */
  /* §9.740 - and never where the person closing owns it by construction: the board's *Vydaná* sends nobody, so the
     server's session rule names whoever pressed it. Off here means the shift list is not even fetched. */
  const asksWhoTookTheMoney =
    closer === 'asked' && paymentAsksWhoTookTheMoney(order.type, channelList)
  /* §9.736 - and whether it may start with the waitress at the till: a pickup, never a table (owner point 13). */
  const startsWithTheCloser = paymentStartsWithTheCloser(order.type, channelList)
  const { data: waiterData, isSuccess: waitersLoaded } = useGetEmployeesWaiters({
    query: { enabled: asksWhoTookTheMoney },
  })
  const waiters = useMemo(() => waiterOptions(waiterData), [waiterData])
  /* §9.736 - read here, above the preselection that reads `user`: a `const` is not hoisted, so the effect's
     dependency list below would be TS2448 against the line this used to be on (§9.487's hazard). */
  const { hasPermission, user } = useAuth()
  /* §9.744, owner O114 - the further discount and *Upraviť* on the one already given ask `order_discount`, the coupon
     `apply_coupon` (BACKEND.md §10A.397: the split and the close carry both as one figure, so the server asks either).
     Read once, so every box below answers the same question as the order builder's. */
  const discountBoxes = discountBoxesFor(hasPermission)

  /**
   * §9.320, owner Q5 - *"čašník to be preselected that one, who was assigned."*
   *
   * The order carries `employeeId`, and nothing read it: §9.128 made naming a waiter mandatory and
   * then asked for a name the order already had. **Preselected, not forced** - the select stays, and
   * the person who took the order is routinely not the person settling it, which is why §9.128 asks
   * at all.
   *
   * **Only when that employee is on today's shift list.** `/employees/waiters` answers who is, and
   * preselecting somebody the server would refuse turns a red asterisk into a failed save. Runs once
   * per order and shift list, and never over a choice somebody has made: `payWaiterId === null` is
   * the guard, so re-picking is never undone by a refetch.
   *
   * §9.736, owner O106 - **otherwise, on a pickup, the signed-in waitress**, when she is on the list
   * (`initialPayWaiterId`, `paymentStartsWithTheCloser`). So a waitress closing a pickup nobody owns, or one a
   * rozvozár is on, pays with herself already chosen, which is the owner's rule in one click. **Never on a
   * table**, which keeps owner point 13 (§9.128). Re-run when the session answers too: `/auth/me` replaces the
   * cached user after the first render.
   */
  useEffect(() => {
    /* §9.740 - never on the board's *Vydaná*: a shift list cached by an earlier visit to the till would otherwise
       preselect somebody, and a named employee wins over the server's session rule (§10A.386). */
    if (closer !== 'asked' || payWaiterId !== null) return
    const initial = initialPayWaiterId(order.employeeId, startsWithTheCloser, user, waiters)
    if (initial !== null) setPayWaiterId(initial)
  }, [closer, order.employeeId, startsWithTheCloser, user, waiters, payWaiterId])
  /**
   * §9.579, FB-883 - the payment method is preselected from the order, exactly as the waiter above is.
   *
   * <p><b>The owner found this one themselves and named it the main issue</b>: <i>"when I tried do ruky
   * I selected Karta - terminál, used «vytvoriť objednávku», modal «Zaplatiť» was there, it was
   * preselected hotovosť, so I think thats the main issue"</i>. The order already carries what they
   * chose and this dialog opened on cash regardless - so an operator who did not notice printed a cash
   * receipt for money that went onto the terminal, and the handover then asked a waiter for cash that
   * was never in the drawer.
   *
   * <p><b>The same omission as §9.320's, on money rather than on a name</b>: that entry's own words are
   * *"asked for a name the order already had"*, and five lines above it does the reading this never
   * did.
   *
   * <p><b>Preselected, not forced</b> - `methodChosen` is the guard, so a person's pick is never undone
   * by a refetch, and a stored method this dialog does not offer is ignored rather than selected: a
   * `<select>`-shaped choice nothing can match would leave no option highlighted at all.
   */
  useEffect(() => {
    if (methodChosen) return
    const stored = order.paymentMethod
    if (stored === undefined || stored === null || stored === '') return
    if (!PAYMENT_METHODS.some((option) => option.value === stored)) return
    setMethod(stored)
  }, [order.paymentMethod, methodChosen])
  /**
   * §9.148 - the owner's point 42b. Nobody says *put it on my tab* at the door; they say it when the
   * bill arrives, which is this dialog.
   */
  const [tabPanelOpen, setTabPanelOpen] = useState(false)
  const [chosenTabId, setChosenTabId] = useState<number | null>(null)
  const [newTabName, setNewTabName] = useState('')
  const { data: tabData } = useGetOrderTabs({ query: { enabled: tabPanelOpen } })
  const tabs = useMemo(() => openTabChoices(tabData), [tabData])
  const slateOffered = naSekeruEnabled(channelList)
  const tabBlock = tabRefusal(channel?.name, channel?.allowsDeferredPayment === true, slateOffered)

  const [isSplitting, setIsSplitting] = useState(false)
  /**
   * How many of each line the leaving guest is paying for, keyed by `order_items.id` - §9.408.
   *
   * **Keyed by the row, not by its position in `order.items`.** That array is refetched every eight
   * seconds by the board underneath this dialog, so a position-keyed tick moves to whatever lands at
   * that index - which is what the restaurant reported: *"kým sa terminál rozsvietil, toto sa
   * označilo samé"*.
   */
  const [selected, setSelected] = useState<Map<number, number>>(new Map())
  const [isWorking, setIsWorking] = useState(false)
  /**
   * The payment on its way, from the press until the dialog closes - `FRONTEND.md` §9.769, owner O130, FB-1086.
   *
   * **While it is set the dialog draws it and nothing else** (`PaymentInFlightView`): the lines, the bill and the
   * figure as they were when the button was pressed, and no control. The split moves the ticked lines to a new order
   * before the receipt prints, and the board re-reads this order every eight seconds, so a dialog drawing from the prop
   * showed the table's remainder and *0,00 €* while the register printed the right receipt. Cleared only when the
   * payment did not happen (a refusal before any money moved); otherwise the dialog closes with it.
   */
  const [inFlight, setInFlight] = useState<PaymentInFlight | null>(null)
  /**
   * §9.366, FB-654 - the close is confirmed before it happens.
   *
   * The owner reported a table closing on one click with the card never charged and the receipt
   * never printed, because `payAll` closes first and asks the terminal afterwards. `'all'` and
   * `'split'` rather than a boolean, so the dialog can name which of the two is about to run.
   */
  /**
   * §9.367 - the question after the payment, and what it was paid.
   *
   * `printed` is carried so the dialog can say the document did not come out without hiding the
   * close: a cash payment may still want the table closed.
   */
  /**
   * §9.447, FB-762 - the question before closing a table with no fiscal document.
   *
   * §9.687, owner #116 (O64) - **a question and an optional reason, not a login.** It was
   * `EmployeeAuthModal`, which asked for an e-mail, a password and a reason; the server now records the
   * signed-in employee on their own `fiskal` or `fiskal_storno` right (`BACKEND.md` §10A.346).
   */
  const [askingToCloseUnprinted, setAskingToCloseUnprinted] = useState(false)
  /* §9.464, answering `OWNER-QUESTIONS.md` #64 - *"Yes - let them edit it there"*. The figure just
   * saved, so the bill is right immediately; `pricedWith` drops it the moment the prop agrees. */
  const [savedDiscountCents, setSavedDiscountCents] = useState<number | null>(null)
  /* §9.806, owner O153 - the loyalty points on this bill. Read only when the order says it holds some, or once this
   * dialog has spent or removed them, so an ordinary bill costs no request. `pointsOverride` is what this dialog just
   * did, held until both the board's order and the server's read agree - the same rule as `savedDiscountCents`, so a
   * bill spent here does not offer the code box again during the eight seconds before the next poll. */
  const [pointsOverride, setPointsOverride] = useState<{ value: PointsOnTheBill | null } | null>(null)
  const orderHoldsPoints = (order.redeemedPoints ?? 0) > 0
  const pointsRead = useGetOrdersOrderIdLoyaltyPoints(order.id ?? 0, {
    query: { enabled: order.id != null && (orderHoldsPoints || pointsOverride !== null) },
  })
  const serverPoints = pointsRead.data?.onTheBill ?? null
  const onTheBill: PointsOnTheBill | null =
    pointsOverride !== null ? pointsOverride.value : orderHoldsPoints ? serverPoints : null
  const pointsCents = onTheBill === null ? 0 : parseMoneyToCents(onTheBill.value)
  useEffect(() => {
    if (pointsOverride === null || pointsRead.isFetching) return
    const want = pointsOverride.value
    const serverAgrees = (serverPoints?.points ?? null) === (want?.points ?? null)
    if (serverAgrees && orderHoldsPoints === (want !== null)) setPointsOverride(null)
  }, [pointsOverride, serverPoints, orderHoldsPoints, pointsRead.isFetching])
  // The same authority as a storno - §9.442 settled this vocabulary. Closing a sale with no fiscal
  // document is the same class of decision as voiding one.
  const mayCloseUnprinted =
    (hasPermission('fiskal') || hasPermission('fiskal_storno')) && mayCloseWithoutDocument(order)

  const [askingToClose, setAskingToClose] = useState<{
    paidCents: number
    printed: boolean
    /* §9.770 - the zľava written on the order before a print that then failed, so the question can say it stayed. */
    keptDiscountCents: number
  } | null>(null)

  /* §9.489, `TODO.md` 3f - the close-table question said `aria-modal="true"` and confined nothing, and it
     was on neither ratchet: `modal-semantics.test.ts` counts the overlays declaring *neither* attribute and
     `modal-focus-wired.test.ts` named only the ones already converted. Guarded on `isWorking` because both
     of its buttons are, and §9.480's rule is that an overlay mid-write may not be dismissed by Escape.
     The close does exactly what the visible cancel does - one exit for the pointer and the keyboard. */
  const closeQuestionRef = useRef<HTMLDivElement>(null)
  const { onKeyDown: closeQuestionKeyDown } = useModalFocus(
    askingToClose !== null,
    closeQuestionRef,
    isWorking
      ? null
      : () => {
          setAskingToClose(null)
          onClose()
        },
  )

  const openTab = usePostOrderTabs()
  const appendToTab = usePostOrderTabsIdOrdersOrderId()

  /**
   * Move the bill to the slate. **Not a payment** - no money is taken, no receipt is printed, and
   * the fiscal document belongs to settlement (§10A.1). The order keeps its channel: a meal eaten at
   * table 4 was sold by the restaurant, and the tab only records that it is not paid for yet.
   */
  const sendToTab = async () => {
    if (tabBlock !== null || isWorking) return
    setIsWorking(true)
    try {
      let tabId = chosenTabId
      if (tabId === null) {
        const name = newTabName.trim()
        if (name === '') {
          toast({ variant: 'destructive', title: tt('Zadajte meno účtu') })
          return
        }
        const opened = (await openTab.mutateAsync({ data: { name } })) as { id?: number }
        if (typeof opened.id !== 'number') {
          toast({ variant: 'destructive', title: tt('Účet sa nepodarilo otvoriť') })
          return
        }
        tabId = opened.id
      }
      await appendToTab.mutateAsync({ id: tabId, orderId: order.id ?? 0 })
      toast({
        title: tt('Presunuté na sekeru'),
        description: tt('Stôl je voľný. Účet sa uhrádza na obrazovke Účty na sekeru.'),
      })
      onPaid()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: tt('Nepodarilo sa presunúť na sekeru'),
        // The server's own words when it has any - `append` refuses with the channel named.
        description: apiErrorMessage(error, tt('Server objednávku odmietol.')),
      })
    } finally {
      setIsWorking(false)
    }
  }
  const [discount, setDiscount] = useState<PayDiscount>(NO_DISCOUNT)
  // §10A.17 - the owner's *"also that you can choose when printing"*. Empty means *do not send one*,
  // which leaves the server to use the channel's configured register, then its capability rule.
  // Deliberately not preselected to what the rule would pick: showing a guess as a choice makes a
  // person confirm something nobody decided.
  const [kasaId, setKasaId] = useState<string>('')
  // §10A.35 - the registers this franchise has, not the two the port shipped with. Empty means
  // *podľa kanála*, which stays the default: the channel's own register is nearly always right.
  const { data: registerData } = useGetFiskalRegisters()
  const registerOptions = useMemo(() => {
    const rows = Array.isArray(registerData)
      ? (registerData as Array<{ kasaId?: string; label?: string }>)
      : []
    return registerIds(registerData).map((id) => ({
      id,
      label: registerName(id, rows, SHIPPED_REGISTER_NAMES),
    }))
  }, [registerData])
  /* §9.740, owner O110 - **a channel both on the premises and driven may name the registers it prints on**, and
     then the select offers exactly those: one is chosen for the person, two or more wait for them (`registerChoice`
     says why). Read through `printRegistersOf`, so a list left on a channel that stopped driving narrows nothing. */
  const kasaChoice = useMemo(() => {
    const rows = Array.isArray(registerData)
      ? (registerData as Array<{ kasaId?: string; label?: string }>)
      : []
    return registerChoice(
      printRegistersOf(order.type ?? '', filterChannelsOf(channelData)),
      registerOptions,
      rows,
    )
  }, [channelData, order.type, registerData, registerOptions])
  /* Whether a person has picked a register - `methodChosen`'s guard for the same reason (§9.579): the channels
     answer after the first render, and a refetch must never undo a choice somebody made. */
  const [kasaChosen, setKasaChosen] = useState(false)
  useEffect(() => {
    /* **Reset as well as set** while nobody has chosen: the channels can answer twice (a cached list, then this
       dialog's own refetch), and a register preselected from the first answer must not survive a second answer
       that names two registers or none. */
    if (kasaChosen) return
    setKasaId(kasaChoice.initial)
  }, [kasaChoice.initial, kasaChosen])
  const kasaBlock = kasaProblem(kasaChoice, kasaId)
  const [couponBusy, setCouponBusy] = useState(false)

  const lines = (order.items ?? []) as PayableLine[]
  const split = splitSelection(lines, selected)
  /* §9.453, FB-776 - **the bill, not the gross.**
   *
   * This was the sum of the lines, on an order whose own `total` already had a zľava subtracted - so
   * a discounted table was invoiced the full price, and a waiter who typed the discount again got a
   * screen that said half and a row that said free. `outstandingCents` subtracts what is already off
   * using the server's own expression, and `payAll` reads `payableCents(discount, dueCents)` for the
   * printed total and the confirmation question (§9.431), so correcting this one value corrects the
   * document, the question, the toast and the payload together. */
  const lineSumCents = lines.reduce(
    (total, line) => total + lineTotalCents(line.unitPrice, line.quantity ?? 0),
    0,
  )
  const paidLinesCents = isSplitting ? split.paidCents : lineSumCents
  /* §9.464 - the order as this dialog prices it, which is the prop until the operator edits its zľava
   * and the figure they saved for as long as the prop disagrees. Every money expression below reads
   * `priced`, deliberately: the bill, the note, the itemised rows and the payload all have to move
   * together, which is the lesson §9.453 paid for. */
  const priced = pricedWith(order, savedDiscountCents)
  const dueCents = outstandingCents(priced, paidLinesCents)
  /* §9.770 - the zľava typed in this dialog and not yet on the order. */
  const typedDiscountCents = totalDiscountCents(discount, dueCents)
  const alreadyOffNote = existingDiscountNote(priced, paidLinesCents)
  /* §9.808 - the order's zľava that is not points, the one line above the fields; `priced`, so a removal shows at once. */
  const otherZlavaCents = zlavaWithoutPointsCents(parseMoneyToCents(priced.discount ?? 0), pointsCents)

  /* **The override clears itself.** A local money figure that outlives the server's is the shape of a
   * money bug; the board polls every eight seconds and `RestaurantPage` refreshes on demand, so the
   * prop catches up and this drops the copy rather than keeping two sources of one number. */
  useEffect(() => {
    if (overrideIsSpent(order, savedDiscountCents)) {
      setSavedDiscountCents(null)
    }
  }, [order, savedDiscountCents])

  /** `/coupons/validate`'s answer for the typed code: what it takes off, or the refusal to print as it comes. */
  const validateCoupon = async (): Promise<{ cents: number } | { error: string }> => {
    try {
      // The same two fields `NewOrderPage` reads: a `valid` flag and `discountAmount`. Read from the payload rather
      // than guessed - the first draft of this looked for a `discount` field, which the endpoint does not answer, so
      // a good coupon silently took nothing off.
      const answer = await postCouponsValidate(couponPayload(discount, dueCents))
      if (answer.valid !== true) return { error: tt('Kupón neplatí pre túto objednávku') }
      return { cents: parseMoneyToCents(answer.discountAmount) }
    } catch (caught) {
      // The backend's own Slovak - "Kupón neexistuje", "Kupón vypršal" - not a guess.
      return { error: apiErrorMessage(caught, tt('Neplatný kupón')) }
    }
  }

  /** The coupon field's OK - coupons only again since §9.808 moved the points into the zľava row. */
  const applyCode = async () => {
    const coupon = await validateCoupon()
    setDiscount((current) =>
      'cents' in coupon
        ? { ...current, couponCents: coupon.cents, couponError: null }
        : { ...current, couponCents: 0, couponError: coupon.error },
    )
  }

  /* §9.808, owner O153 - *"where you choose %, € - add also «BODY»"*: the customer's code and how many points, in the
   * zľava row. The sixth digit asks the server whose code it is and the most the bill can take; OK spends the number
   * typed. Every rule is the server's (BACKEND.md §10A.452). */
  const [pointsMode, setPointsMode] = useState(false)
  const [pointsCode, setPointsCode] = useState('')
  const [pointsDraft, setPointsDraft] = useState('')
  const [pointsQuote, setPointsQuote] = useState<{
    customerName: string
    balance: number
    maxPoints: number
    maxValue: number
  } | null>(null)
  const [pointsError, setPointsError] = useState<string | null>(null)
  const mayUsePoints = order.id != null && onTheBill === null && !order.fiskalPrintedAt

  const typePointsCode = (typed: string) => {
    setPointsCode(typed)
    setPointsQuote(null)
    setPointsError(null)
    const digits = codeDigits(typed)
    if (digits === null || order.id == null) return
    void (async () => {
      try {
        const quote = await postOrdersOrderIdLoyaltyPointsQuote(order.id ?? 0, { code: digits })
        setPointsQuote(quote)
        setPointsDraft(String(quote.maxPoints))
      } catch (caught) {
        setPointsError(apiErrorMessage(caught, tt('Kód sa nepodarilo overiť.')))
      }
    })()
  }

  const spendPoints = async () => {
    const digits = codeDigits(pointsCode)
    const typed = pointsDraft.trim()
    const points = /^\d+$/.test(typed) ? Number(typed) : 0
    if (digits === null || points <= 0) {
      setPointsError(tt('Zadajte kód zákazníka a počet bodov.'))
      return
    }
    if (pointsQuote !== null && points > pointsQuote.maxPoints) {
      setPointsError(`${tt('Na tento účet najviac')} ${formatCount(pointsQuote.maxPoints)} ${tt('bodov')}.`)
      return
    }
    setCouponBusy(true)
    try {
      const applied = await postOrdersOrderIdLoyaltyPoints(order.id ?? 0, { code: digits, points })
      setSavedDiscountCents(parseMoneyToCents(applied.discount))
      setPointsOverride({ value: applied.onTheBill })
      void pointsRead.refetch()
      onOrderChanged?.()
      toast({ title: tt('Vernostné body uplatnené'), description: onTheBillNote(applied.onTheBill) })
      setPointsMode(false)
      setPointsCode('')
      setPointsDraft('')
      setPointsQuote(null)
      setPointsError(null)
    } catch (caught) {
      setPointsError(apiErrorMessage(caught, tt('Body sa nepodarilo uplatniť.')))
    } finally {
      setCouponBusy(false)
    }
  }

  /** *Odstrániť* on the points line: off the bill and back to the customer, before the receipt prints. */
  const removePoints = async () => {
    if (couponBusy || onTheBill === null) return
    setCouponBusy(true)
    try {
      await deleteOrdersOrderIdLoyaltyPoints(order.id ?? 0)
      setSavedDiscountCents(Math.max(0, parseMoneyToCents(priced.discount ?? 0) - pointsCents))
      setPointsOverride({ value: null })
      void pointsRead.refetch()
      onOrderChanged?.()
      toast({ title: tt('Body odstránené a vrátené zákazníkovi') })
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Body sa nepodarilo odstrániť'),
        description: apiErrorMessage(caught, tt('Server ich odmietol.')),
      })
    } finally {
      setCouponBusy(false)
    }
  }

  /**
   * *Odstrániť* on the zľava line - §9.808: the order's zľava without its points, taken off through `PATCH
   * /orders/{id}` (the server re-derives `total`, §10A.397). Points keep theirs and come off with their own button.
   */
  const removeExistingZlava = async () => {
    if (isWorking) return
    setIsWorking(true)
    try {
      await patchOrdersOrderId(
        order.id ?? 0,
        pointsCents > 0
          ? editedDiscountWithPoints('', user?.name ?? null, pointsCents)
          : editedDiscountPayload('', user?.name ?? null),
      )
      setSavedDiscountCents(pointsCents)
      toast({ title: tt('Zľava odstránená') })
      onOrderChanged?.()
    } catch (error) {
      toast({
        variant: 'destructive',
        title: tt('Zľavu sa nepodarilo odstrániť'),
        description: apiErrorMessage(error, tt('Server objednávku odmietol.')),
      })
    } finally {
      setIsWorking(false)
    }
  }

  const setQuantity = (index: number, delta: number) => {
    setSelected((current) => {
      const next = new Map(current)
      const line = lines[index]
      const limit = line?.quantity ?? 0
      const key = lineKey(line, index)
      const value = Math.max(0, Math.min(limit, (current.get(key) ?? 0) + delta))
      if (value === 0) next.delete(key)
      else next.set(key, value)
      return next
    })
  }

  /** The register's own warnings, minus the ones that mean "reconnecting". */
  const reportKasa = (response: FiskalReceiptPrinted) => {
    const warning = response.warning
    if (warning && !isTransientKasaWarning(warning)) {
      toast({
        title: tt('Upozornenie kasy'),
        description: typeof warning === 'string' ? warning : JSON.stringify(warning),
      })
    }
    if (response.paperOut === true) {
      toast({
        variant: 'destructive',
        title: tt('Papier chýba v kase'),
        description: tt('Doklad bol uzavretý – dotlačí sa automaticky po vložení papiera.'),
      })
    }
  }

  /**
   * §9.367 - it says whether it printed.
   *
   * It swallowed every failure into a toast and returned nothing, which is what let `payAll` close
   * the table over a document that never came out - the owner's *"bloček also did not printed and it
   * was closed"*.
   */
  const printReceipt = async (
    target: { id?: number; orderNumber?: string; type?: string },
    items: PayableLine[],
    /* §9.770, FB-1088 - **the figures the order holds, given by the caller.** This decided the discount itself -
     * the typed zľava alone whenever one was typed, and the order's own only when none was - so an order carrying a
     * zľava and given a further one registered the lines less the further one alone (Šurany's 15983: total 18,00,
     * registered 20,30). The whole bill passes the zľava it has just written on the order, a split the one the server
     * wrote on its portion. */
    money: { totalCents: number; discountCents: number },
  ): Promise<boolean> => {
    try {
      const response = await postFiskalReceipt({
        orderId: target.id ?? 0,
        orderNumber: target.orderNumber ?? '',
        orderType: target.type,
        // Only sent when somebody picked one. An empty string would read as a register named "".
        ...(kasaId === '' ? {} : { kasaId }),
        paymentMethod: method,
        total: centsToEuros(money.totalCents),
        // The receipt carries the discount the order holds, so the printed document and the order agree about it.
        ...(money.discountCents > 0 ? { discount: centsToEuros(money.discountCents) } : {}),
        // §9.390 - the channel decides how the food is taxed, not the three names.
        items: receiptLines(items, target.type, channel),
      })

      printIfVirtual(response)
      reportKasa(response)
      return true
    } catch (caught) {
      /**
       * **A receipt that already exists is not a failure to print one** - §9.362, FB-649.
       *
       * `FiskalReceiptService` answers 409 `ALREADY_PRINTED` with the document's number in the
       * sentence, and this said *"Pokladňa - chyba"* in red over it. The order is closed either way
       * - the line below is why - so there is nothing for the operator to do except know that the
       * document is out, which is what the server said.
       *
       * The path that produces this without a genuine duplicate is a first attempt whose answer was
       * lost: the owner's 2026-09-18 incident, where the kasa printed and the connection did not
       * survive long enough to say so.
       */
      if (alreadyPrinted(caught)) {
        toast({
          title: tt('Doklad už bol vytlačený'),
          description: alreadyPrintedNote(caught),
        })
        // The document exists, which is what the caller needs to know - §9.362.
        return true
      }
      // The order is already closed; a failed print does not un-sell the food.
      toast({
        variant: 'destructive',
        title: tt('Pokladňa - chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa vytlačiť doklad.')),
      })
      return false
    }
  }

  const paySplit = async () => {
    if (!hasSomethingToPay(selected)) return
    /* §9.769, O130 - **what was pressed, taken before anything is awaited.** The split below moves these lines to a
     * new order, so from the next read of the table they are not on this order at all; the dialog keeps drawing them,
     * and the figure, until it closes. */
    const flight: PaymentInFlight = {
      kind: 'split',
      lines: frozenLines(lines, selected),
      payCents: payableCents(discount, dueCents),
      rows: billRows(priced, paidLinesCents, totalDiscountCents(discount, dueCents)),
      printed: [],
    }
    setInFlight(flight)
    setIsWorking(true)
    try {
      const response = (await postOrdersSplitPayment({
        originalOrderId: order.id ?? 0,
        paymentMethod: method,
        paidItems: split.paidItems,
        remainingItems: split.remainingItems,
        /* §9.394 - the discount the receipt is about to print. Without it the register printed one
         * total and the order closed at another. **`dueCents`, not `split.paidCents`**: it is the
         * expression the receipt uses, so the base cannot drift between the document and the order.
         *
         * §9.453 corrected what that expression means - the selected lines **less the share of the
         * order's existing zľava** rather than their sum - and the server measures the typed
         * percentage against the same subtraction, which is the whole point of mirroring it. */
        ...closeDiscountPayload(discount, dueCents),
      })) as Record<string, unknown>

      /* §9.770, FB-1088 - the portion as the server wrote it: its `discount` is the share of the order's zľava plus
       * the typed one, and its `total` is what it costs - which is what the receipt registers. */
      const paid = paidPortionOf(response)
      const chargedCents = paid?.totalCents ?? flight.payCents
      if (paid) {
        await printReceipt(paid, paid.items ?? split.paidItems, {
          totalCents: chargedCents,
          discountCents: paid.discountCents,
        })
      }

      toast({
        title: tt('Zaplatené'),
        /* §9.769, FB-1089 - what was charged. It was `split.paidCents`, the ticked lines before any zľava, so a
         * discounted split's toast named a figure nobody paid. */
        description: `${formatCents(chargedCents)} · ${
          response.hasRemaining === true ? tt('stôl zostáva otvorený') : tt('stôl uzavretý')
        }`,
      })
      onPaid()
      onClose()
    } catch (caught) {
      // Refused before any money moved: the dialog is drawn again as it was, so the operator can correct it.
      setInFlight(null)
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa spracovať platbu.')),
      })
    } finally {
      setIsWorking(false)
    }
  }

  /** The whole bill is a split where everything is selected. */
  // §9.736 - the answer the shift-list fetch was enabled on, so the question is never drawn over a list nobody
  // fetched (§9.652's worse half).
  const needsWaiter = asksWhoTookTheMoney && !isSplitting
  const waiterBlock = payWaiterProblem(needsWaiter, waitersLoaded, waiters, payWaiterId)
  /* §9.740, owner O111 - who the close names: at the till, the answer to the waiter question; on the board's
     *Vydaná*, the person who pressed it (*"assign it to someone, who use Vydaná button"*), so the server's rule for a
     named closer gives them the order (BACKEND.md §10A.386). */
  const closerId = closer === 'whoever-closes' ? closerEmployeeId(user) : payWaiterId

  /**
   * §9.367 - print, then ask about the table.
   *
   * The owner: *"after payment it will ask you 'Chcete zatvoriť stôl?' like in old system"*, and
   * *"make sure you can pay the order in reštaurácia without zatvoriť stôl, so it will be visible in
   * kuchyňa"*. So the document goes out first, and the close is a second decision that may be
   * answered *no* - which leaves the order where the kitchen can still see it.
   *
   * The receipt payload no longer needs the close's answer: `paidOrder ?? order` fell back to the
   * order itself for a whole bill, so this is a reordering rather than a restructure.
   */
  /**
   * Record the method the operator picked, before anything is printed - §9.602, FB-906.
   *
   * **The order is already settled by the time this dialog opens on a counter sale.** A
   * `settlesImmediately` channel creates it `delivered` carrying the *builder's* method, so the method chosen
   * here reaches the row only through `stampOrder` on a successful print or `closeFinalPortion` on the close -
   * and the owner's report is what happens when neither runs: the register answered 503 and the closing
   * question was left on screen, so the archive showed *Hotovosť* on a sale taken by card.
   *
   * **Only when it differs**, so an ordinary payment sends no extra request; and **never blocking** - a
   * refused patch leaves both existing routes exactly as they were, which is what makes this an additional
   * chance to record the method rather than a new dependency for taking money.
   *
   * **Before the print, deliberately.** §10A.304 refuses a method change once `fiskal_printed_at` is set.
   * This goes through `OrderUpdateService`, where that guard does not live, and it runs first regardless.
   */
  const recordChosenMethod = async () => {
    if (order.paymentMethod === method) return
    try {
      await patchOrdersOrderId(order.id ?? 0, { paymentMethod: method })
    } catch {
      // Deliberately silent: the close still carries the method, and a toast here would fire on every
      // blip while telling the operator nothing they can act on.
    }
  }

  /**
   * Who the zľava written on the order is recorded as given by - §9.770, the close's own order (`provenanceOf`): the
   * waiter named on the payment, else whoever is signed in.
   */
  const discountOperator = (): string | null =>
    waiters.find((waiter) => waiter.id === closerId)?.name ?? user?.name ?? null

  const payAll = async () => {
    /* §9.431, the owner's report - **`payableCents`, not the line sum.**
     *
     * `everything.paidCents` is the bill before the zľava, so the register printed the gross and
     * the *Prebehla platba úspešne?* question confirmed the gross, while `closeTheTable` sent
     * `closeDiscountPayload` and the order closed at the discounted figure. §9.394's comment on the
     * split path names this exactly - *"the register printed one total and the order closed at
     * another"* - and the whole-bill path kept doing it.
     *
     * The same expression *Na zaplatenie* shows, so the document, the question and the figure the
     * operator agreed to cannot drift apart. */
    const toPayCents = payableCents(discount, dueCents)
    /* §9.770, O130 - **the zľava typed here is written on the order before the receipt prints.** It reached the order
     * only with *Áno, zatvoriť stôl*, so a table left open kept its full total on every screen while the receipt was
     * discounted, and every later close took the full price. After the print the server refuses a money edit
     * (§10A.292), so it goes first; `null` when nothing was typed, and an ordinary payment sends nothing extra. */
    /* The order's own zľava, whole, as it stands - the figure the write replaces, so nothing already on the order can
     * be lost by it, and the figure the receipt has always printed when nothing was typed. On a whole bill it is
     * `existingDiscountCents` exactly; read from the column rather than through the share, so an order whose stored
     * subtotal disagrees with its lines keeps its zľava rather than having it replaced by a share. */
    const ownCents = Math.max(0, parseMoneyToCents(priced.discount ?? 0))
    const recordBody = recordedDiscountPayload(discount, dueCents, ownCents, discountOperator())
    // FB-1088 - the receipt carries the zľava the order holds: its own, plus the one just written.
    const orderDiscountCents =
      recordBody === null ? ownCents : recordedDiscountCents(ownCents, discount, dueCents)
    /* §9.769 - what was pressed, before anything is awaited: every line, and the lines the close will pay. */
    setInFlight({
      kind: 'all',
      lines: frozenLines(lines, wholeBill(lines)),
      payCents: toPayCents,
      rows: billRows(priced, lineSumCents, totalDiscountCents(discount, dueCents)),
      printed: lines,
    })
    setSelected(wholeBill(lines))
    setIsSplitting(false)
    setIsWorking(true)
    try {
      await recordChosenMethod()
      if (recordBody !== null) {
        try {
          await patchOrdersOrderId(order.id ?? 0, recordBody)
        } catch (refused) {
          /* Refused - a right, or a document already out - so nothing is printed and nothing charged, and the
           * dialog is drawn again as it was. The server's own sentence says which. */
          setInFlight(null)
          toast({
            variant: 'destructive',
            title: tt('Zľava sa nezapísala - doklad sa nevytlačil'),
            description: apiErrorMessage(refused, tt('Server zľavu odmietol.')),
          })
          return
        }
        /* §9.464's override: the dialog prices from the figure just written until the board's read agrees, and the
         * box is emptied, so nothing on this screen can take the zľava off a second time. */
        setSavedDiscountCents(orderDiscountCents)
        setDiscount(NO_DISCOUNT)
      }
      const printed = await printReceipt(order, lines, {
        totalCents: toPayCents,
        discountCents: orderDiscountCents,
      })
      // The question, whatever the printer said - and it says so when the document did not come out,
      // because a cash payment may still want the table closed.
      setAskingToClose({
        paidCents: toPayCents,
        printed,
        keptDiscountCents: recordBody === null ? 0 : orderDiscountCents,
      })
    } catch (caught) {
      setInFlight(null)
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa spracovať platbu.')),
      })
    } finally {
      setIsWorking(false)
    }
  }

  /**
   * *Áno, zatvoriť stôl* - §9.367.
   *
   * **The same call `payAll` used to make, with the same payload.** Only when it runs changed, and
   * whether it runs at all: answering *no* leaves the table open and the order in the kitchen.
   */
  /**
   * *Áno, zatvoriť stôl* with no fiscal document - §9.457.
   *
   * **The owner:** *"When do ruky is closed without printing anything, instead of doručená make it
   * 'Zrušená'"*. On a table the §9.447 close means *the printer is broken and the money was taken*,
   * so `delivered` is the truth; on a counter sale the receipt **is** the transaction, and recording
   * one nobody printed as delivered puts money in the day's takings that nobody collected.
   *
   * A `PATCH` rather than `split-payment`, because the two are different events: `split-payment`
   * settles money and this is the absence of a settlement. Keyed on `settlesImmediately` and never
   * on the slug - §9.393.
   */

  const closeTheTable = async (paidCents: number) => {
    /* §9.769 - **after *Zaplatiť všetko*, the lines that were printed**, not the prop's: the board re-reads the order
     * under the question, and a line added in between (another waiter's round, §9.620 keeping the document) was closed
     * as paid against a receipt that does not carry it. Refused with a sentence when the order no longer holds them;
     * the server refuses the same mismatch when this copy of the order is behind (`refuseUnlessConserved`). §9.369's
     * *Zatvoriť stôl* has no payment in flight and closes the order as it stands, its receipt printed earlier. */
    const paying = inFlight?.kind === 'all' ? inFlight.printed : lines
    if (inFlight?.kind === 'all' && !sameBill(inFlight.printed, lines)) {
      toast({
        variant: 'destructive',
        title: tt('Stôl sa nezatvoril'),
        description: orderChangedNote(paidCents),
      })
      return
    }
    setIsWorking(true)
    try {
      const everything = splitSelection(paying, wholeBill(paying))
      await postOrdersSplitPayment({
        originalOrderId: order.id ?? 0,
        paymentMethod: method,
        paidItems: everything.paidItems,
        remainingItems: [],
        ...(closerId === null ? {} : { employeeId: closerId }),
        /* §9.770, O130 - **no zľava here.** A zľava typed in this dialog is written on the order before its receipt
         * prints (`payAll`), and `closeFinalPortion` keeps a discount it is not given; sending it again would take
         * it off twice. §9.394's wire for the whole bill is that write now, not this close. */
      })
      toast({ title: tt('Zaplatené'), description: formatCents(paidCents) })
      setAskingToClose(null)
      onPaid()
      onClose()
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Nepodarilo sa uzatvoriť stôl.')),
      })
    } finally {
      setIsWorking(false)
    }
  }

  /**
   * Close with no fiscal document - §9.687, owner #116 (O64), `BACKEND.md` §10A.346.
   *
   * **No credentials are sent and none are collected**: the server records the signed-in employee, on
   * their own `fiskal` or `fiskal_storno` right, and stores the reason only when one was typed. The
   * settled-or-cancelled decision stays the server's (§9.468) - it owns `sales_channels` and answers
   * `outcome`.
   */
  const closeUnprinted = async (reason: string) => {
    if (isWorking) return
    setIsWorking(true)
    try {
      const body = closeWithoutDocumentBody({
        reason,
        paymentMethod: method,
        employeeId: closerId,
        discount: closeDiscountPayload(discount, dueCents),
      })
      const response = await postOrdersOrderIdCloseWithoutDocument(order.id ?? 0, body)
      setAskingToCloseUnprinted(false)
      /* The name the **server** recorded, which is whoever is signed in - *Zatvoril*, not *Overil*: since
       * #116 nobody is verified, somebody closes it. */
      const closedBy = closedByName(response)
      toast({
        title: tt('Zatvorené bez fiškálneho dokladu'),
        description:
          closedBy === ''
            ? formatCents(payableCents(discount, dueCents))
            : `${formatCents(payableCents(discount, dueCents))} · ${tt('Zatvoril')}: ${closedBy}`,
      })
      /* §9.457's rule still decides what the operator is told, and the **server** decides which happened.
       * `outcome` is `cancelled` for a channel that settles immediately, because a *Do ruky* sale with no
       * document took no money - unless that channel's `unprintedCloseCancels` is off (§9.726, §10A.374), and
       * then it is `closed` and only the toast above is shown. */
      if (closedOutcome(response) === 'cancelled') {
        toast({
          title: tt('Objednávka zrušená'),
          description: tt('Predaj na ruku bez dokladu neprijal žiadne peniaze.'),
        })
      }
      onPaid()
      onClose()
    } catch (caught) {
      toast({
        variant: 'destructive',
        title: tt('Stôl sa nepodarilo zatvoriť'),
        description: apiErrorMessage(caught, tt('Server zatvorenie bez dokladu odmietol.')),
      })
    } finally {
      setIsWorking(false)
    }
  }

  /* §9.367, FB-655 - *Prebehla platba úspešne?* once the document is out, as legacy asks it (`restaurant.tsx:3370`).
     Answering *no* leaves the table open and the order in the kitchen, which is the owner's *"pay -> waiting for food"*.
     A value rather than inline, because §9.769's in-flight view draws it over the snapshot as well. */
  const closeQuestion =
    askingToClose !== null &&
    ((question) => (
      <div
        role="dialog"
        aria-modal="true"
        aria-label={question.title}
        data-close-table-question
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      >
        <div
          ref={closeQuestionRef}
          onKeyDown={closeQuestionKeyDown}
          className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4"
        >
          <div>
            <h3 className="text-base font-bold text-foreground">{question.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{question.description}</p>
            {/* §9.770 - a print that failed after the zľava was written keeps it on the order, and says so. */}
            {!askingToClose.printed && askingToClose.keptDiscountCents > 0 && (
              <p
                className="text-sm font-semibold text-amber-600 dark:text-amber-400 mt-1"
                data-close-table-discount-kept
              >
                {discountKeptNote(askingToClose.keptDiscountCents)}
              </p>
            )}
          </div>
          <button
            type="button"
            data-close-table-yes
            disabled={isWorking}
            onClick={() => void closeTheTable(askingToClose.paidCents)}
            className="w-full py-3 rounded-xl font-bold bg-emerald-500 text-white flex items-center justify-center gap-2 hover:bg-emerald-400 transition-colors disabled:opacity-50"
          >
            {isWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {question.closeLabel}
          </button>
          <button
            type="button"
            data-close-table-keep-open
            /* The opening focus lands here, not on *close the table* - §9.441's rule, and this is the
               dialog it was written for: a reflexive Enter on a question about a table with a document
               already out must not be the one that closes it. */
            data-dialog-cancel
            disabled={isWorking}
            onClick={() => {
              // **Nothing is called.** The document is out, the table stays open, and the
              // kitchen keeps the order - the owner pressed cancel here expecting exactly this.
              setAskingToClose(null)
              onClose()
            }}
            className="w-full py-3 rounded-xl font-bold border border-border text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
          >
            {question.keepOpenLabel}
          </button>
        </div>
      </div>
    ))(
      closeTableQuestion(
        // The order's number: `OrderListItem` carries no table name, and the dialog is
        // already open over the table the operator picked.
        `#${order.orderNumber ?? ''}`,
        formatCents(askingToClose.paidCents),
        { printed: askingToClose.printed },
      ),
    )

  /* §9.769, O130 - **a payment on its way draws what was pressed and nothing else**, with the question after the whole
     bill over it. Below every hook, so the dialog's hooks run the same on every render. */
  if (inFlight !== null) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <PaymentInFlightView
          flight={inFlight}
          orderLabel={`#${order.orderNumber ?? ''} · ${order.customerName ?? ''}`}
        />
        {closeQuestion}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border flex-shrink-0">
          <Receipt className="w-5 h-5 text-primary" />
          <div className="flex-1 min-w-0">
            <p className="font-bold">
              <T>Zaplatiť</T>
            </p>
            <p className="text-xs text-muted-foreground truncate">
              #{order.orderNumber} · {order.customerName}
            </p>
            {/* §9.54 - the note, where the food is handed over. *Faktúru na firmu* is for whoever
             * settles it, and this is the screen that settles it. */}
            {order.notes && (
              <p className="text-[11px] text-amber-600 mt-0.5" data-order-note>
                📝 {order.notes}
              </p>
            )}
          </div>
          <button
            type="button"
            aria-label={tt('Zatvoriť')}
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-muted transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>
        </div>

        {order.status === 'cancelled' && (
          <p className="px-5 py-2 bg-red-500/10 border-b border-red-500/30 text-xs font-bold text-red-500">
            <T>Objednávka bola zrušená – nie je možné ju uzatvoriť.</T>
          </p>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <button
            type="button"
            onClick={() => {
              setIsSplitting((splitting) => !splitting)
              setSelected(new Map())
            }}
            className={cn(
              'w-full flex items-center justify-center gap-2 py-2 rounded-xl border text-xs font-bold transition-colors',
              isSplitting
                ? 'bg-primary/10 border-primary text-primary'
                : 'border-border text-muted-foreground hover:text-foreground',
            )}
          >
            <Split className="w-3.5 h-3.5" /> <T>Rozdeliť účet</T>
          </button>

          <div className="space-y-1.5" data-pay-lines>
            {lines.map((line, index) => {
              const chosen = selected.get(lineKey(line, index)) ?? 0
              return (
                <div
                  // §9.408 - the row's own id, so React keeps a line's DOM node with the line rather
                  // than with the slot it happened to occupy when the board last refreshed.
                  key={lineKey(line, index)}
                  // Published so the identity is observable from outside: a spec, or somebody
                  // reading the DOM over a waiter's shoulder, can see which row a tick belongs to
                  // rather than inferring it from the order lines happen to be drawn in.
                  data-pay-line={lineKey(line, index)}
                  data-pay-line-chosen={chosen}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl bg-muted/50"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      <T>{line.productName ?? line.customName ?? ''}</T>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {line.quantity}× {formatCents(0 + Number(line.unitPrice) * 100)}
                    </p>
                  </div>
                  {isSplitting ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        aria-label={`${tt('Menej')} ${line.productName ?? ''}`}
                        onClick={() => setQuantity(index, -1)}
                        className="w-6 h-6 rounded-lg bg-background border border-border flex items-center justify-center"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="w-5 text-center text-sm font-bold tabular-nums">
                        {chosen}
                      </span>
                      <button
                        type="button"
                        aria-label={`${tt('Viac')} ${line.productName ?? ''}`}
                        onClick={() => setQuantity(index, 1)}
                        className="w-6 h-6 rounded-lg bg-background border border-border flex items-center justify-center"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <span className="text-sm font-bold tabular-nums">
                      {formatCents(lineTotalCents(line.unitPrice, line.quantity ?? 0))}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        <div className="border-t border-border px-5 py-4 space-y-3 flex-shrink-0">
          <div className="space-y-2 px-4 pb-2" data-pay-discount>
            {/* §9.808, owner O153 - *"Remove that «Zľava už na objednávke ... - zostáva ...» ... make sure they will use
              * Odstrániť"*. One plain line for a zľava already on the order that is not points (points have their own
              * line below), and Odstrániť takes it off; what is still due is *Na zaplatenie*'s to say. */}
            {otherZlavaCents > 0 && (
              <p className="text-[11px] text-muted-foreground" data-existing-discount>
                {tt('Zľava na objednávke')}: {formatCents(otherZlavaCents)}
                {discountBoxes.discount && !isSplitting && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      disabled={isWorking}
                      className="underline hover:no-underline disabled:opacity-50"
                      data-remove-existing-discount
                      onClick={() => void removeExistingZlava()}
                    >
                      {tt('Odstrániť')}
                    </button>
                  </>
                )}
              </p>
            )}
            {/* §9.744 - the further discount on `order_discount`. Not drawn rather than emptied: `discount` starts at
              * nothing and only this box changes it, so a hidden box sends no discount at all. */}
            {discountBoxes.discount && (
              <>
              <div className="flex gap-2">
                <select
                  value={pointsMode && mayUsePoints ? 'points' : discount.mode}
                  onChange={(event) => {
                    // §9.808 - *Body*: the typed % or € is cleared, so nothing is given twice.
                    if (event.target.value === 'points') {
                      setPointsMode(true)
                      setDiscount((current) => ({ ...current, value: '' }))
                      return
                    }
                    setPointsMode(false)
                    setDiscount((current) => ({
                      ...current,
                      mode: event.target.value as DiscountMode,
                    }))
                  }}
                  aria-label={tt('Typ zľavy')}
                  data-discount-mode
                  className="min-h-[40px] rounded-xl bg-card border border-border px-2 text-sm"
                >
                  <option value="percent">%</option>
                  <option value="fixed">€</option>
                  {mayUsePoints && <option value="points">{tt('Body')}</option>}
                </select>
                {pointsMode && mayUsePoints ? (
                  <>
                    <input
                      value={pointsCode}
                      onChange={(event) => typePointsCode(event.target.value)}
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={7}
                      placeholder={tt('Kód zákazníka')}
                      aria-label={tt('Kód zákazníka')}
                      data-points-code
                      className="w-32 min-h-[40px] rounded-xl bg-card border border-border px-3 text-sm tabular-nums"
                    />
                    <input
                      value={pointsDraft}
                      onChange={(event) => setPointsDraft(event.target.value)}
                      inputMode="numeric"
                      placeholder={tt('Koľko bodov')}
                      aria-label={tt('Koľko bodov')}
                      data-points-amount
                      className="flex-1 min-w-0 min-h-[40px] rounded-xl bg-card border border-border px-3 text-sm tabular-nums"
                    />
                    <button
                      type="button"
                      data-points-apply
                      disabled={couponBusy || codeDigits(pointsCode) === null}
                      onClick={() => void spendPoints()}
                      className="min-h-[40px] px-4 rounded-xl bg-card border border-border text-sm font-bold disabled:opacity-50"
                    >
                      <T>OK</T>
                    </button>
                  </>
                ) : (
                <input
                  value={discount.value}
                  onChange={(event) =>
                    setDiscount((current) => ({ ...current, value: event.target.value }))
                  }
                  inputMode="decimal"
                  /* §9.453 - *further*, because it is added to whatever the order already carries and
                   * is measured against what is left rather than against the gross. */
                  placeholder={alreadyOffNote === null ? tt('Zľava') : tt('Ďalšia zľava')}
                  aria-label={tt('Zľava')}
                  data-discount-value
                  className="flex-1 min-h-[40px] rounded-xl bg-card border border-border px-3 text-sm tabular-nums"
                />
                )}
              </div>
              {pointsMode && mayUsePoints && pointsQuote !== null && (
                <p className="text-[11px] text-muted-foreground" data-points-quote>
                  {pointsQuote.customerName}: {formatCount(pointsQuote.balance)} {tt('bodov')} ·{' '}
                  {tt('na tento účet najviac')} {formatCount(pointsQuote.maxPoints)} (
                  {formatCents(parseMoneyToCents(pointsQuote.maxValue))})
                </p>
              )}
              {pointsMode && mayUsePoints && pointsError !== null && (
                <p className="text-[11px] text-destructive" data-points-error>
                  {pointsError}
                </p>
              )}
              </>
            )}
            {/* §9.47 - the coupon half of `PayDiscount`, which has existed since it was written and was
             * offered nowhere. The server decides: `/coupons/validate` knows what has expired, been used
             * up or does not reach its minimum, and its refusal is printed as it comes.
             * §9.744 - on `apply_coupon`. */}
            {discountBoxes.coupon && (
              <div className="flex gap-2">
                <input
                  value={discount.couponCode}
                  onChange={(event) =>
                    // Typing again clears the previous answer: a code that has been edited has not been
                    // validated, and leaving the old discount on screen would apply it to a different code.
                    setDiscount((current) => ({
                      ...current,
                      couponCode: event.target.value,
                      couponCents: 0,
                      couponError: null,
                    }))
                  }
                  placeholder={tt('Kód kupóna')}
                  aria-label={tt('Kód kupóna')}
                  data-pay-coupon
                  className="flex-1 min-h-[40px] rounded-xl bg-card border border-border px-3 text-sm uppercase"
                />
                <button
                  type="button"
                  data-pay-coupon-apply
                  disabled={discount.couponCode.trim() === '' || couponBusy}
                  onClick={() => {
                    void (async () => {
                      setCouponBusy(true)
                      try {
                        await applyCode()
                      } finally {
                        setCouponBusy(false)
                      }
                    })()
                  }}
                  className="min-h-[40px] px-4 rounded-xl bg-card border border-border text-sm font-bold disabled:opacity-50"
                >
                  <T>OK</T>
                </button>
              </div>
            )}
            {discount.couponError !== null && (
              <p className="text-[11px] text-destructive" data-pay-coupon-error>
                {discount.couponError}
              </p>
            )}
            {/* §9.807 - the customer's points on the bill, one plain line under the field they were typed into. */}
            {onTheBill !== null && (
              <p className="text-[11px] text-muted-foreground" data-points-on-bill>
                {onTheBillNote(onTheBill)}
                {onTheBill.removable && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      disabled={couponBusy}
                      className="underline hover:no-underline disabled:opacity-50"
                      data-points-remove
                      onClick={() => void removePoints()}
                    >
                      {tt('Odstrániť')}
                    </button>
                  </>
                )}
              </p>
            )}

            {discountProblems(discount, dueCents).map((problem) => (
              <p key={problem} className="text-[11px] text-destructive" data-discount-problem>
                {problem}
              </p>
            ))}
            {/* The line under the fields, so it goes with them: with neither box it would only repeat *Na zaplatenie*. */}
            {(discountBoxes.discount || discountBoxes.coupon) && (
              <p className="text-[11px] text-muted-foreground" data-discount-note>
                {discountNote(discount, dueCents)}
              </p>
            )}
          </div>

          {/* §9.453, FB-776 - the owner: *"in zaplatit fix that also price with zlava will be
            * shown"*. One bold *Na zaplatenie* said what to collect and never said why it differed
            * from the menu, so a discounted table looked like a mis-priced one. Empty on an
            * ordinary bill, where the line below already says everything. */}
          {billRows(priced, paidLinesCents, totalDiscountCents(discount, dueCents)).map((row) => (
            <div
              key={row.label}
              className="flex items-center justify-between text-xs text-muted-foreground"
              data-bill-row
            >
              <span>{row.label}</span>
              <span className={cn('tabular-nums', row.off && 'text-amber-600 dark:text-amber-400')}>
                {row.off ? '-' : ''}
                {formatCents(row.cents)}
              </span>
            </div>
          ))}

          <div className="flex items-center justify-between font-bold">
            <span>
              <T>Na zaplatenie</T>
            </span>
            <span className="text-xl text-primary tabular-nums" data-pay-due>
              {formatCents(payableCents(discount, dueCents))}
            </span>
          </div>
          {needsWaiter && (
            <div className="mb-2" data-pay-waiter-field>
              <p
                className={cn(
                  'text-[10px] font-bold uppercase tracking-wider mb-1.5 flex items-center gap-1',
                  payWaiterId === null ? 'text-red-500' : 'text-muted-foreground',
                )}
              >
                <T>Čašník / obsluha</T>
                {payWaiterId === null && <span>*</span>}
              </p>
              {waiters.length === 0 && waitersLoaded ? (
                // An empty dropdown reads as a broken screen; the fix is in attendance.
                <p
                  className="text-xs font-semibold text-red-500 bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2"
                  data-pay-waiter-none
                >
                  {waiterBlock}
                </p>
              ) : (
                <select
                  value={payWaiterId === null ? '' : String(payWaiterId)}
                  onChange={(event) =>
                    setPayWaiterId(event.target.value === '' ? null : Number(event.target.value))
                  }
                  aria-label={tt('Čašník / obsluha')}
                  data-pay-waiter
                  className={cn(
                    'w-full px-3 py-2.5 rounded-xl bg-background border text-sm font-semibold outline-none',
                    payWaiterId === null ? 'border-red-500/60 text-red-500' : 'border-border',
                  )}
                >
                  <option value="">{tt('- vybrať čašníka -')}</option>
                  {waiters.map((waiter) => (
                    <option key={waiter.id} value={waiter.id}>
                      {waiter.name}
                    </option>
                  ))}
                </select>
              )}
              {waiterBlock !== null && waiters.length > 0 && (
                <p className="text-[10px] text-red-500 mt-1 font-semibold" data-pay-waiter-block>
                  {waiterBlock}
                </p>
              )}
            </div>
          )}
          {/* §9.148 - the third door, beside cash and card.
           *
           * **§9.299 reversed this entry's own prediction.** It said the button should be drawn even
           * when the channel forbids it, *"because an option that vanishes cannot be told apart from
           * one that was never built - which is exactly the report that would come back"*. The
           * report that came back was the opposite, twice: *"Na sekeru is still visible there in that
           * «Zaplatiť» menu, when kanal is disabled, hide it also from there."*
           *
           * So the two refusals are told apart. A channel that is **off** hides the door; every other
           * refusal still draws it disabled with its reason, because *"not for this order"* is worth
           * saying and *"not in this restaurant"* is not. */}
          {!isSplitting && slateOffered && (
            <div className="mb-2" data-pay-tab-section>
              {tabPanelOpen ? (
                <div className="rounded-xl border border-border bg-background p-3 space-y-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <T>Na ktorý účet</T>
                  </p>
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      data-tab-choice={tab.id}
                      onClick={() => setChosenTabId(tab.id)}
                      className={cn(
                        'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border text-sm',
                        chosenTabId === tab.id
                          ? 'bg-primary/10 border-primary text-primary'
                          : 'bg-card border-border',
                      )}
                    >
                      <span className="truncate font-semibold">{tab.name}</span>
                      <span className="tabular-nums text-xs">
                        {formatCents(tab.outstandingCents)}
                      </span>
                    </button>
                  ))}
                  <div className="flex items-center gap-2">
                    <input
                      value={newTabName}
                      onChange={(event) => {
                        setNewTabName(event.target.value)
                        setChosenTabId(null)
                      }}
                      placeholder={tt('… alebo nový účet na meno')}
                      aria-label={tt('Nový účet na meno')}
                      data-new-tab-name
                      className="flex-1 min-h-[38px] rounded-lg bg-card border border-border px-3 text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => void sendToTab()}
                    disabled={isWorking || (chosenTabId === null && newTabName.trim() === '')}
                    data-confirm-tab
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-amber-500 text-white font-bold text-sm disabled:opacity-50"
                  >
                    {isWorking ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <NotebookPen className="w-4 h-4" />
                    )}
                    <T>Presunúť na sekeru</T>
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setTabPanelOpen(true)}
                  disabled={tabBlock !== null || order.status === 'cancelled'}
                  data-pay-to-tab
                  title={tabBlock ?? undefined}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-amber-500/40 text-amber-500 font-bold text-sm disabled:opacity-50"
                >
                  <NotebookPen className="w-4 h-4" /> <T>Na sekeru</T>
                </button>
              )}
              {/* §9.299 - the note goes with the button. A reason for a control nobody can see is
               * worse than silence. */}
              {slateOffered && tabBlock !== null && (
                <p className="text-[10px] text-muted-foreground mt-1" data-pay-tab-block>
                  {tabBlock}
                </p>
              )}
            </div>
          )}

          {/* §9.320, owner Q5 - *"platba hotovosť karta to be at the bottom before zaplatiť
           * všetko… KASA change keep there, but add it there, more compact"*.
           *
           * **This is not a reversal of §9.203.** That entry moved the method row *above* the bill
           * because below it a table of twenty pushed it off the screen. This footer is
           * `flex-shrink-0`, so a long bill cannot push it anywhere - which is the same requirement
           * met better, and it puts the decision beside the button that acts on it.
           *
           * Compact: one row of two, not two full-width buttons, and the register's paragraph of
           * hint text becomes the select's own first option - *podľa kanála* is what it explained. */}
          <div className="grid grid-cols-2 gap-2" data-pay-methods>
            {PAYMENT_METHODS.map((option) => (
              <button
                key={option.value}
                type="button"
                data-pay-method={option.value}
                onClick={() => {
                  setMethod(option.value)
                  setMethodChosen(true)
                }}
                className={cn(
                  'flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-sm font-semibold border transition-all',
                  method === option.value
                    ? 'bg-primary/10 border-primary text-primary'
                    : 'bg-background border-border text-muted-foreground hover:text-foreground',
                )}
              >
                <option.icon className="w-4 h-4 shrink-0" />
                {option.label}
              </button>
            ))}
          </div>

          {/* §10A.17 - *Podľa kanála* sends no `kasaId` at all, which leaves the server to use the
           * channel's configured register and then its capability rule; that is what every close
           * does today. Not preselected to whichever the server would choose: showing a guess as a
           * choice makes somebody confirm a decision nobody made. */}
          <label className="flex items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground shrink-0">
              <T>Kasa</T>
            </span>
            <select
              value={kasaId}
              onChange={(event) => {
                setKasaId(event.target.value)
                setKasaChosen(true)
              }}
              aria-label={tt('Kasa')}
              data-pay-kasa
              data-pay-kasa-listed={kasaChoice.listed}
              className={cn(
                'flex-1 min-h-[36px] rounded-xl bg-card border px-2 text-xs',
                kasaBlock === null ? 'border-border' : 'border-red-500/60 text-red-500',
              )}
            >
              {/* §9.740 - a channel that names its registers has no *Podľa kanála*: the person printing picks one of
                * them. The empty option stays only as the unchosen state, so the select never shows a register
                * nobody picked (§9.562's blank-select trap the other way round). */}
              <option value="" disabled={kasaChoice.listed}>
                {kasaChoice.listed ? tt('- vyberte kasu -') : tt('Podľa kanála')}
              </option>
              {/* §10A.35 - every configured register, so a payment can be rung up on a third. */}
              {kasaChoice.options.map((register) => (
                <option key={register.id} value={register.id}>
                  {register.label}
                </option>
              ))}
            </select>
          </label>
          {kasaBlock !== null && (
            <p className="text-[10px] text-red-500 font-semibold" data-pay-kasa-block>
              {kasaBlock}
            </p>
          )}

          {/* §9.369, FB-657 - a table paid at the till and left open for the food. The order says so
           * itself (`stampOrder` writes `fiskalPrintedAt`), so closing it later is offered here
           * rather than requiring a second payment the server would refuse with 409. */}
          {alreadyPaid(order) && (
            <div className="space-y-2" data-already-paid>
              <p className="text-xs text-muted-foreground">{alreadyPaidNote(order)}</p>
              {/* §9.770 - **a zľava typed here cannot reach a receipt that is already printed**, and the close no
                * longer carries one, because every other zľava is on the order before its receipt. So the close is
                * held while one is typed, and said: *Zaplatiť všetko* would be refused by the server for the same
                * reason (§10A.292), in its own words. */}
              {typedDiscountCents > 0 && (
                <p className="text-xs font-semibold text-amber-600 dark:text-amber-400" data-discount-after-print>
                  {discountAfterPrintNote()}
                </p>
              )}
              <button
                type="button"
                data-close-paid-table
                disabled={isWorking || order.status === 'cancelled' || typedDiscountCents > 0}
                onClick={() => void closeTheTable(dueCents)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-400 transition-colors disabled:opacity-50"
              >
                {isWorking ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                <T>Zatvoriť stôl</T>
              </button>
            </div>
          )}

          {/* §9.447, FB-762 - the owner's "Close without fiškal doklad". The gate is the exact
           * inverse of the block above, so exactly one of the two is ever offered. 614 of the
           * owner's closed restaurant and bar orders are already in this state, arrived at by the
           * printer failing rather than by anybody choosing it. */}
          {mayCloseUnprinted && (
            <div className="space-y-2" data-close-unprinted-block>
              <button
                type="button"
                data-close-without-document
                disabled={isWorking}
                onClick={() => setAskingToCloseUnprinted(true)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-amber-500/30 bg-amber-500/10 text-amber-600 font-bold text-sm hover:bg-amber-500/20 transition-colors disabled:opacity-50"
              >
                <Ban className="w-4 h-4" />
                <T>Zatvoriť bez fiškálneho dokladu</T>
              </button>
              <p className="text-xs text-muted-foreground" data-close-without-document-note>
                {closeWithoutDocumentNote(unprintedClose)}
              </p>
            </div>
          )}

          <button
            type="button"
            // §9.367 - the whole bill prints and then asks about the table; a split pays its part
            // and leaves the table open by construction, so it needs no second question.
            onClick={() => void (isSplitting ? paySplit() : payAll())}
            disabled={
              isWorking ||
              order.status === 'cancelled' ||
              // §9.453 - *nothing is selected*, which is the lines and not the bill: a portion whose
              // zľava covers it is 0,00 to pay and still a portion somebody is settling.
              (isSplitting && paidLinesCents === 0) ||
              waiterBlock !== null ||
              // §9.740 - a channel that names its registers prints on one somebody chose.
              kasaBlock !== null ||
              discountProblems(discount, dueCents).length > 0
            }
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isWorking ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Receipt className="w-4 h-4" />
            )}
            {isSplitting ? <T>Zaplatiť vybrané</T> : <T>Zaplatiť všetko</T>}
          </button>

          {/* §9.442, FB-761 - the owner asked for it "when using «Zaplatiť» storno there". Under
           * the pay button rather than beside it: the dialog's whole purpose is to take money, and
           * a reversal sitting next to *Zaplatiť všetko* at the same size is a mis-tap waiting to
           * happen. It renders nothing unless a document exists and nobody has reversed it. */}
          <FiskalStornoAction
            order={order}
            onDone={onPaid}
            compact
            label={tt('Storno fiškálneho dokladu')}
            className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-red-500/20 bg-red-500/10 text-red-500 text-xs font-bold hover:bg-red-500/20 transition-colors"
          />
        </div>
      </div>

      {/* §9.367, FB-655 - *Prebehla platba úspešne?* once the document is out, as legacy asks it
       * (`restaurant.tsx:3370`). Answering *no* leaves the table open and the order in the kitchen,
       * which is the owner's *"pay -> waiting for food"*. */}
      {askingToCloseUnprinted && (
        <CloseWithoutDocumentConfirm
          /* §9.687, owner #116 (O64) - *"bez zadávania prihlasovacieho údajov a zadania dôvodu"*. One
           * question with an optional reason, posting straight to the endpoint that records who and
           * closes (`BACKEND.md` §10A.346); who is the signed-in employee, on their own right.
           *
           * **Still named after the consequence rather than *are you sure*** (§9.447), and still the
           * figure being collected rather than the bill before the zľava (§9.453). */
          title={closeWithoutDocumentTitle(
            `#${order.orderNumber ?? ''}`,
            formatCents(payableCents(discount, dueCents)),
          )}
          note={closeWithoutDocumentNote(unprintedClose)}
          close={unprintedClose}
          busy={isWorking}
          onCancel={() => setAskingToCloseUnprinted(false)}
          onConfirm={(reason) => void closeUnprinted(reason)}
        />
      )}

      {closeQuestion}
    </div>
  )
}
